import React, { useState, useEffect } from "react";
import * as styles from "./hobbyGrid.css.ts";
import { useLanguage } from "../../../../../hooks";
import { useTheme } from "../../../../shared/context/indexSharedContext";

// ✅ CORRECTION MAJEURE - Utiliser la nouvelle API hiérarchique
import { apiService } from "../../../../shared/services/apiService";
import { normalizeImageUrl } from "../../../../shared/utils/imageUrl";
import type { HobbyTheme } from '../../../../../types';

// Type pour les éléments de loisir adaptés à l'affichage
interface DisplayHobbyItem {
  id: string;
  category_fr: string;
  category_en: string;
  reference_fr: string;
  reference_en: string;
  description_fr?: string;
  description_en?: string;
  icon_image_url?: string;
  reference_image_url?: string;
  order_index: number;
  is_visible: boolean;
  animation_duration?: number;
  // ✅ NOUVEAU - Pour stocker les éléments d'un thème
  items?: any[];
}

const HobbyGrid: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const [hobbies, setHobbies] = useState<DisplayHobbyItem[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ✅ FONCTION POUR OBTENIR L'ICÔNE APPROPRIÉE SELON LE THÈME
  const getThemeIcon = (themeName: string): string => {
    const name = themeName.toLowerCase();
    if (name.includes('film') || name.includes('movie') || name.includes('cinéma')) return '🎬';
    if (name.includes('jeu') || name.includes('game') || name.includes('vidéo')) return '🎮';
    if (name.includes('livre') || name.includes('book') || name.includes('lecture')) return '📚';
    if (name.includes('musique') || name.includes('music')) return '🎵';
    if (name.includes('série') || name.includes('tv') || name.includes('show')) return '📺';
    if (name.includes('sport')) return '⚽';
    if (name.includes('voyage') || name.includes('travel')) return '✈️';
    if (name.includes('cuisine') || name.includes('food')) return '🍳';
    if (name.includes('art') || name.includes('peinture')) return '🎨';
    if (name.includes('photo')) return '📷';
    return '🎯'; // Icône par défaut
  };

  // ✅ FONCTION POUR OBTENIR LES BONNES COULEURS SELON LE THÈME
  const getThemeColors = () => {
    return {
      // Couleur pour les éléments de liste selon le thème
      listItemColor: theme === 'light' ? '#4a5568' : '#e2e8f0',
      // Couleur pour les éléments "plus..." selon le thème  
      moreItemsColor: theme === 'light' ? '#718096' : '#94a3b8',
      // Couleur du titre "Mes favoris" selon le thème
      favoritesTitle: theme === 'light' ? '#0066cc' : '#64ffda',
      // Couleur de la bordure selon le thème
      borderColor: theme === 'light' ? 'rgba(0, 102, 204, 0.2)' : 'rgba(100, 255, 218, 0.2)'
    };
  };

  useEffect(() => {
    loadHobbies();
  }, []);

  // ✅ FALLBACK DATA FUNCTION - Thèmes avec leurs éléments (sans notes ni années)
  const getFallbackHobbies = (): DisplayHobbyItem[] => [
    {
      id: 'fallback-theme-1',
      category_fr: 'Thème',
      category_en: 'Theme',
      reference_fr: 'Films',
      reference_en: 'Movies',
      description_fr: 'Mes films préférés de tous les temps',
      description_en: 'My favorite movies of all time',
      icon_image_url: '', // ✅ CORRECTION - Pas d'URL hardcodée
      reference_image_url: '', // ✅ CORRECTION - Pas d'URL hardcodée
      order_index: 0,
      is_visible: true,
      animation_duration: 6,
      items: [
        { id: '1', name_fr: 'Inception', name_en: 'Inception', is_visible: true },
        { id: '2', name_fr: 'Matrix', name_en: 'The Matrix', is_visible: true },
        { id: '3', name_fr: 'Interstellar', name_en: 'Interstellar', is_visible: true },
        { id: '4', name_fr: 'Le Parrain', name_en: 'The Godfather', is_visible: true }
      ]
    },
    {
      id: 'fallback-theme-2',
      category_fr: 'Thème',
      category_en: 'Theme',
      reference_fr: 'Jeux Vidéo',
      reference_en: 'Video Games',
      description_fr: 'Mes jeux vidéo favoris',
      description_en: 'My favorite video games',
      icon_image_url: '', // ✅ CORRECTION - Pas d'URL hardcodée
      reference_image_url: '', // ✅ CORRECTION - Pas d'URL hardcodée
      order_index: 1,
      is_visible: true,
      animation_duration: 5,
      items: [
        { id: '5', name_fr: 'The Witcher 3', name_en: 'The Witcher 3', is_visible: true },
        { id: '6', name_fr: 'Cyberpunk 2077', name_en: 'Cyberpunk 2077', is_visible: true },
        { id: '7', name_fr: 'Red Dead Redemption 2', name_en: 'Red Dead Redemption 2', is_visible: true }
      ]
    },
    {
      id: 'fallback-theme-3',
      category_fr: 'Thème',
      category_en: 'Theme',
      reference_fr: 'Musique',
      reference_en: 'Music',
      description_fr: 'Mes artistes et compositeurs préférés',
      description_en: 'My favorite artists and composers',
      icon_image_url: '', // ✅ CORRECTION - Pas d'URL hardcodée
      reference_image_url: '', // ✅ CORRECTION - Pas d'URL hardcodée
      order_index: 2,
      is_visible: true,
      animation_duration: 7,
      items: [
        { id: '8', name_fr: 'Pink Floyd', name_en: 'Pink Floyd', is_visible: true },
        { id: '9', name_fr: 'Hans Zimmer', name_en: 'Hans Zimmer', is_visible: true },
        { id: '10', name_fr: 'Daft Punk', name_en: 'Daft Punk', is_visible: true }
      ]
    }
  ];

  const loadHobbies = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Chargement des thèmes de loisirs...');

      // ✅ CORRECTION MAJEURE - Utiliser la nouvelle API hiérarchique
      const response = await apiService.get('/hobby-themes/with-items', true, {
        cacheTtlMs: 240000, // 4 minutes cache pour les hobbies
        useCache: true
      });
      
      console.log('📊 Response thèmes avec éléments:', response);
      
      if (response?.data) {
  const themes: HobbyTheme[] = response.data as any;
        console.log('✅ Thèmes récupérés:', themes.length);
        
        // ✅ NOUVELLE LOGIQUE - Convertir les THÈMES (pas les éléments individuels)
        const displayThemes: DisplayHobbyItem[] = themes
          .filter(theme => theme.is_visible) // Filtrer les thèmes visibles
          .map((theme, index): DisplayHobbyItem => ({
            id: theme.id,
            category_fr: 'Thème', // Catégorie générique pour les thèmes
            category_en: 'Theme',
            reference_fr: theme.name_fr || theme.name || 'Thème sans nom',
            reference_en: theme.name_en || theme.name || 'Unnamed Theme',
            description_fr: theme.description_fr || '',
            description_en: theme.description_en || '',
            // Le backend renvoie `image_url` (et éventuellement `theme_image_url` dans d'autres endpoints)
            icon_image_url: normalizeImageUrl((theme as any).image_url || (theme as any).theme_image_url || ''),
            reference_image_url: normalizeImageUrl((theme as any).image_url || (theme as any).theme_image_url || ''),
            order_index: theme.order_index || index,
            is_visible: theme.is_visible,
            animation_duration: 5 + (index % 3),
            // Les items sont renvoyés sous la clé `hobby_items` côté backend
            // On garde un fallback sur `items` si présent
            items: Array.isArray((theme as any).hobby_items)
              ? (theme as any).hobby_items
              : (Array.isArray((theme as any).items) ? (theme as any).items : [])
          }))
          .sort((a, b) => a.order_index - b.order_index);
        
        console.log('✅ Thèmes convertis pour affichage:', displayThemes.length);
        setHobbies(displayThemes);
        
        if (displayThemes.length === 0) {
          console.warn('⚠️ Aucun thème visible trouvé, utilisation du fallback');
          setHobbies(getFallbackHobbies());
        }
      } else {
        console.warn('⚠️ Réponse API non valide, utilisation du fallback');
        console.warn('📊 Response complète:', response);
        setHobbies(getFallbackHobbies());
      }
    } catch (err) {
      console.error('❌ Erreur chargement nouveaux loisirs:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setHobbies(getFallbackHobbies());
    } finally {
      setLoading(false);
    }
  };

  /**
   * Determines the optimal overlay position
   */
  const getOverlayClass = (_index: number): string => {
    // Infobulle affichée vers le BAS de la pastille, avec la liste complète.
    return styles.hobbyInfoOverlay;
  };

  /**
   * Get field value with fallback
   */
  const getFieldValue = (hobby: DisplayHobbyItem, field: keyof DisplayHobbyItem, fallback: string = ''): string => {
    const value = hobby[field];
    return value ? String(value) : fallback;
  };

  // États de chargement et d'erreur
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Chargement des loisirs...</p>
        </div>
      </div>
    );
  }

  if (error && (!hobbies || hobbies.length === 0)) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>❌ Erreur: {error}</p>
          <button 
            onClick={() => loadHobbies()} 
            className={styles.retryButton}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // ✅ CORRECTION ULTRA-CRITIQUE - Triple vérification avant map
  if (!hobbies || !Array.isArray(hobbies) || hobbies.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p>Aucun loisir à afficher</p>
          <button 
            onClick={() => loadHobbies()} 
            className={styles.retryButton}
          >
            Recharger
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} role="region" aria-label="Centres d'intérêt">
      <h2 className={styles.title} id="hobbies-title">
        {language === 'fr' ? 'Mes Centres d\'Intérêt' : 'My Hobbies'}
      </h2>

      <div className={styles.floatingCirclesContainer} aria-labelledby="hobbies-title">
        {hobbies.map((hobby, index) => (
          <div
            key={hobby.id}
            className={`${styles.hobbyItem} ${active === index ? styles.active : ''}`}
            onMouseEnter={() => setActive(index)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(index)}
            onBlur={() => setActive(null)}
            tabIndex={0}
            role="button"
            aria-describedby={`hobby-${index}-description`}
            style={{ '--animation-order': index } as React.CSSProperties}
          >
            {/* Conteneur d'image principale */}
            <div className={styles.hobbyImageContainer}>
              {hobby.icon_image_url ? (
                <img
                  src={hobby.icon_image_url}
                  alt={language === 'fr' 
                    ? getFieldValue(hobby, 'reference_fr', hobby.category_fr) 
                    : getFieldValue(hobby, 'reference_en', hobby.category_en)
                  }
                  className={styles.hobbyImage}
                  onError={(e) => {
                    console.warn(`❌ Erreur chargement image pour ${hobby.id}:`, hobby.icon_image_url);
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="${styles.hobbyImagePlaceholder}">
                          ${getThemeIcon(hobby.reference_fr)}
                        </div>
                      `;
                    }
                  }}
                />
              ) : (
                <div className={styles.hobbyImagePlaceholder}>
                  {getThemeIcon(hobby.reference_fr)}
                </div>
              )}
            </div>

            {/* Label de catégorie - affiche le nom du thème */}
            <div className={styles.categoryLabel}>
              {language === 'fr' ? hobby.reference_fr : hobby.reference_en}
            </div>

            {/* Overlay d'information détaillée - affiche les éléments du thème */}
            <div 
              className={getOverlayClass(index)}
              id={`hobby-${index}-description`}
              role="tooltip"
            >
              {/* Titre du thème */}
              <h4 className={styles.hobbyTitle}>
                {language === 'fr' ? hobby.reference_fr : hobby.reference_en}
              </h4>
              
              {/* Description du thème */}
              {((language === 'fr' && hobby.description_fr) || (language === 'en' && hobby.description_en)) && (
                <p className={styles.hobbyDescription}>
                  {language === 'fr' ? hobby.description_fr : hobby.description_en}
                </p>
              )}
              
              {/* ✅ NOUVEAU - Liste simple des éléments favoris */}
              {hobby.items && hobby.items.length > 0 && (
                <div style={{ marginTop: '0.5rem', borderTop: `1px solid ${getThemeColors().borderColor}`, paddingTop: '0.5rem' }}>
                  <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: getThemeColors().favoritesTitle, fontWeight: '500', textAlign: 'center' }}>
                    {language === 'fr' ? 'Mes favoris :' : 'My favorites:'}
                  </h5>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', textAlign: 'left' }}>
                    {hobby.items
                      .filter((item: any) => item.is_visible)
                      .map((item: any, itemIndex: number) => (
                        <li 
                          key={item.id || itemIndex}
                          style={{ 
                            fontSize: '0.75rem', 
                            color: getThemeColors().listItemColor,
                            margin: '0.15rem 0',
                            padding: '0.1rem 0',
                            fontWeight: '400'
                          }}
                        >
                          • {language === 'fr' ? (item.name_fr || item.title_fr) : (item.name_en || item.title_en)}
                        </li>
                      ))
                    }
                  </ul>
                </div>
              )}
            </div>

            {/* Texte alternatif pour lecteurs d'écran */}
            <span className={styles.srOnly}>
              {language === 'fr' 
                ? `Thème ${hobby.reference_fr}: ${hobby.description_fr || ''}. ${hobby.items?.length || 0} éléments.`
                : `Theme ${hobby.reference_en}: ${hobby.description_en || ''}. ${hobby.items?.length || 0} items.`
              }
            </span>
          </div>
        ))}
      </div>
      
      {/* ✅ Indicateur si utilisation du fallback */}
      {error && (
        <div className={styles.fallbackIndicator}>
          ⚠️ {language === 'fr' ? 'Données de démonstration utilisées' : 'Demo data used'}
        </div>
      )}
    </div>
  );
};

// Export nommé ET export par défaut pour plus de compatibilité
export { HobbyGrid };
export default HobbyGrid;